import { Component, For, Show, createEffect, createSignal, onCleanup } from 'solid-js'
import Button from '../../../jsx/Button'
import type { ActionBinding } from '../ActionBinding.type'
import createActionBinding from '../createActionBinding'
import createKeyboardBinding from '../createKeyboardBinding'
import createMouseClickBinding from '../createMouseClickBinding'
import { InputActionType } from '../InputActionType'
import type { AnyInputBinding } from '../InputBinding/InputBindingUnion.type'
import { InputBindingDevice } from "../InputBindingDevice.enum"
import type { KeyCode } from '../KeyCode.type'
import { MouseButton } from '../MouseButton.enum'

interface Props {
  action: InputActionType
  existingBindings: ActionBinding[]
  onSave: (bindings: ActionBinding[]) => void
  onCancel: () => void
}

const KeybindingEditor: Component<Props> = (props) => {
  const [newBindings, setNewBindings] = createSignal<ActionBinding[]>([])
  const [isListening, setIsListening] = createSignal(false)
  const [listeningFor, setListeningFor] = createSignal<'keyboard' | 'mouse' | 'gamepad' | null>(null)

  const handleStartListening = (type: 'keyboard' | 'mouse' | 'gamepad') => {
    setIsListening(true)
    setListeningFor(type)
    setNewBindings([])
  }

  const handleStopListening = () => {
    setIsListening(false)
    setListeningFor(null)
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!isListening() || listeningFor() !== 'keyboard') return
    
    event.preventDefault()
    event.stopPropagation()

    // Ignore modifier keys alone
    if (['Control', 'Alt', 'Shift', 'Meta'].includes(event.key)) {
      return
    }

    const code = event.code as KeyCode
    const binding = createActionBinding(
      props.action,
      createKeyboardBinding(
        code,
        event.shiftKey,
        event.ctrlKey || event.metaKey,
        event.altKey
      )
    )

    setNewBindings(prev => {
      // Check if this exact binding already exists
      const exists = prev.some(b => 
        b.input.device === InputBindingDevice.Keyboard &&
        b.input.code === code &&
        b.input.shift === event.shiftKey &&
        b.input.ctrl === (event.ctrlKey || event.metaKey) &&
        b.input.alt === event.altKey
      )
      if (exists) return prev
      return [...prev, binding]
    })
  }

  const handleMouseDown = (event: MouseEvent) => {
    if (!isListening() || listeningFor() !== 'mouse') return
    
    event.preventDefault()
    event.stopPropagation()

    const binding = createActionBinding(
      props.action,
      createMouseClickBinding(event.button as MouseButton)
    )

    setNewBindings(prev => {
      const exists = prev.some(b => 
        b.input.device === InputBindingDevice.Mouse &&
        b.input.button === event.button
      )
      if (exists) return prev
      return [...prev, binding]
    })
  }

  // const handleGamepadButtonDown = (event: GamepadEvent): void => {
  //   if (!isListening() || listeningFor() !== 'gamepad') return
    
  //   // Gamepad button detection is more complex - would need to poll gamepad state
  //   // For now, we'll skip this in the first implementation
  // }

  // Set up event listeners reactively
  createEffect(() => {
    const listening = isListening()
    const forType = listeningFor()
    
    if (listening && forType === 'keyboard') {
      window.addEventListener('keydown', handleKeyDown, true)
      onCleanup(() => {
        window.removeEventListener('keydown', handleKeyDown, true)
      })
    }
    
    if (listening && forType === 'mouse') {
      window.addEventListener('mousedown', handleMouseDown, true)
      onCleanup(() => {
        window.removeEventListener('mousedown', handleMouseDown, true)
      })
    }
    
    // if (listening && forType === 'gamepad') {
    //   window.addEventListener('gamepadbuttondown', handleGamepadButtonDown, true)
    //   onCleanup(() => {
    //     window.removeEventListener('gamepadbuttondown', handleGamepadButtonDown as any, true)
    //   })
    // }
  })

  const handleSave = () => {
    props.onSave(newBindings())
    handleStopListening()
  }

  const handleCancel = () => {
    handleStopListening()
    props.onCancel()
  }

  const handleRemoveBinding = (index: number) => {
    setNewBindings(prev => prev.filter((_, i) => i !== index))
  }

  const formatBinding = (input: AnyInputBinding): string => {
    if (input.device === InputBindingDevice.Keyboard) {
      const parts: string[] = []
      if (input.ctrl) parts.push('Ctrl')
      if (input.alt) parts.push('Alt')
      if (input.shift) parts.push('Shift')
      parts.push(formatKeyCode(input.code))
      return parts.join(' + ')
    } else if (input.device === InputBindingDevice.Mouse) {
      const buttonNames = ['Left', 'Middle', 'Right', 'Back', 'Forward']
      return `Mouse ${buttonNames[input.button] || `Button ${input.button}`}`
    } else if (input.device === InputBindingDevice.Gamepad) {
      return `Gamepad Button ${input.button}`
    } else if (input.device === InputBindingDevice.Touch) {
      return 'Touch'
    }
    return 'Unknown'
  }

  const formatKeyCode = (code: string): string => {
    if (code.startsWith('Key')) return code.slice(3)
    if (code.startsWith('Digit')) return code.slice(5)
    if (code === 'Space') return 'Space'
    if (code === 'Enter') return 'Enter'
    if (code === 'Escape') return 'Esc'
    if (code === 'ArrowUp') return '↑'
    if (code === 'ArrowDown') return '↓'
    if (code === 'ArrowLeft') return '←'
    if (code === 'ArrowRight') return '→'
    return code
  }

  return (
    <div class="keybinding-editor">
      <Show when={!isListening()}>
        <div class="mb-2">
          <Button
            class="btn btn-sm btn-primary me-2"
            onClick={() => handleStartListening('keyboard')}
          >
            Record Keyboard
          </Button>
          <Button
            class="btn btn-sm btn-primary me-2"
            onClick={() => handleStartListening('mouse')}
          >
            Record Mouse
          </Button>
          <Button
            class="btn btn-sm btn-primary me-2"
            onClick={() => handleStartListening('gamepad')}
            disabled
          >
            <span title="Gamepad support coming soon">
              Record Gamepad
            </span>
          </Button>
        </div>
      </Show>
      
      <Show when={isListening()}>
        <div class="alert alert-info mb-2">
          Listening for {listeningFor()} input... Press keys or click to record. Click "Stop" when done.
        </div>
        <Button
          class="btn btn-sm btn-secondary mb-2"
          onClick={handleStopListening}
        >
          Stop Listening
        </Button>
      </Show>

      <Show when={newBindings().length > 0}>
        <div class="mb-2">
          <strong>New bindings:</strong>
          <For each={newBindings()}>
            {(binding, index) => (
              <div class="d-inline-block me-2 mb-1">
                <span class="badge bg-primary">
                  {formatBinding(binding.input)}
                  <button
                    class="btn-close btn-close-white ms-1"
                    style={{ 'font-size': '0.6em' }}
                    onClick={() => handleRemoveBinding(index())}
                  />
                </span>
              </div>
            )}
          </For>
        </div>
      </Show>

      <div class="d-flex gap-2">
        <Button
          class="btn btn-sm btn-success"
          onClick={handleSave}
          disabled={newBindings().length === 0}
        >
          Save
        </Button>
        <Button
          class="btn btn-sm btn-secondary"
          onClick={handleCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  )
}

export default KeybindingEditor

